import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from '../lib/axios';

export type TokenPackCurrency = 'USD' | 'VND';

export interface TokenPackPrice {
  publicId: string;
  currency: TokenPackCurrency;
  amountMinor: string;
  price: number;
  isActive: boolean;
}

export interface TokenPack {
  publicId: string;
  code: string;
  name: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  tokenAmount: number;
  price: number;
  currency: TokenPackCurrency;
  prices: TokenPackPrice[];
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TokenPackPriceInput {
  currency: TokenPackCurrency;
  amountMinor: number;
  isActive: boolean;
}

export interface CreateTokenPackInput {
  code: string;
  name: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  tokenAmount: number;
  prices: TokenPackPriceInput[];
  isActive: boolean;
  sortOrder: number;
}

export interface UpdateTokenPackInput {
  publicId: string;
  code?: string;
  name?: string;
  descriptionEn?: string | null;
  descriptionVi?: string | null;
  tokenAmount?: number;
  prices?: TokenPackPriceInput[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateTokenPackStatusInput {
  publicId: string;
  isActive: boolean;
}

export interface AdvanceTokenPurchaseLimitConfig {
  dailyTokenLimit: number | null;
  monthlyTokenLimit: number | null;
}

export interface AdvanceTokenPurchaseLimitResponse {
  config: AdvanceTokenPurchaseLimitConfig;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const tokenPackKeys = {
  all: ['token-packs'] as const,
  lists: () => [...tokenPackKeys.all, 'list'] as const,
  purchaseLimits: () => [...tokenPackKeys.all, 'purchase-limits'] as const,
};

type BackendTokenPack = Omit<TokenPack, 'prices'> & {
  prices?: Array<{
    publicId: string;
    currency: TokenPackCurrency;
    amountMinor: string | number;
    isActive: boolean;
  }>;
};

const amountMinorToPrice = (amountMinor: string | number, currency: TokenPackCurrency) => {
  const value = Number(amountMinor);
  return currency === 'USD' ? value / 100 : value;
};

const normalizeTokenPack = (tokenPack: BackendTokenPack): TokenPack => ({
  ...tokenPack,
  descriptionEn: tokenPack.descriptionEn ?? null,
  descriptionVi: tokenPack.descriptionVi ?? null,
  prices:
    tokenPack.prices && tokenPack.prices.length > 0
      ? tokenPack.prices.map((price) => ({
          ...price,
          amountMinor: price.amountMinor.toString(),
          price: amountMinorToPrice(price.amountMinor, price.currency),
        }))
      : [
          {
            publicId: `${tokenPack.publicId}_legacy_price`,
            currency: tokenPack.currency,
            amountMinor: Math.round(
              tokenPack.currency === 'USD' ? tokenPack.price * 100 : tokenPack.price
            ).toString(),
            price: tokenPack.price,
            isActive: true,
          },
        ],
});

const toTokenPackRequest = <
  T extends CreateTokenPackInput | Omit<UpdateTokenPackInput, 'publicId'>,
>(
  data: T
): T => {
  const request = { ...(data as Record<string, unknown>) };

  (['descriptionEn', 'descriptionVi'] as const).forEach((field) => {
    if (field in request) {
      const value = request[field];
      request[field] = typeof value === 'string' && value.trim() === '' ? null : (value ?? null);
    }
  });

  return request as T;
};

const fetchTokenPacks = async (): Promise<TokenPack[]> => {
  const response =
    await axiosInstance.get<ApiResponse<BackendTokenPack[]>>('/v1/admin/token-packs');
  return (response.data.data || []).map(normalizeTokenPack);
};

const createTokenPack = async (data: CreateTokenPackInput): Promise<TokenPack> => {
  const response = await axiosInstance.post<ApiResponse<BackendTokenPack>>(
    '/v1/admin/token-packs',
    toTokenPackRequest(data)
  );
  return normalizeTokenPack(response.data.data as BackendTokenPack);
};

const updateTokenPack = async ({ publicId, ...data }: UpdateTokenPackInput): Promise<TokenPack> => {
  const response = await axiosInstance.patch<ApiResponse<BackendTokenPack>>(
    `/v1/admin/token-packs/${publicId}`,
    toTokenPackRequest(data)
  );
  return normalizeTokenPack(response.data.data as BackendTokenPack);
};

const updateTokenPackStatus = async ({
  publicId,
  isActive,
}: UpdateTokenPackStatusInput): Promise<TokenPack> => {
  const response = await axiosInstance.patch<ApiResponse<BackendTokenPack>>(
    `/v1/admin/token-packs/${publicId}/status`,
    { isActive }
  );
  return normalizeTokenPack(response.data.data as BackendTokenPack);
};

const fetchAdvanceTokenPurchaseLimits = async (): Promise<AdvanceTokenPurchaseLimitResponse> => {
  const response = await axiosInstance.get<ApiResponse<AdvanceTokenPurchaseLimitResponse>>(
    '/v1/admin/token-packs/purchase-limits'
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Advance token purchase limits response is empty');
  }

  return response.data.data;
};

const updateAdvanceTokenPurchaseLimits = async (
  config: AdvanceTokenPurchaseLimitConfig
): Promise<AdvanceTokenPurchaseLimitResponse> => {
  const response = await axiosInstance.put<ApiResponse<AdvanceTokenPurchaseLimitResponse>>(
    '/v1/admin/token-packs/purchase-limits',
    config
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Advance token purchase limits response is empty');
  }

  return response.data.data;
};

export const useTokenPacks = () => {
  return useQuery({
    queryKey: tokenPackKeys.lists(),
    queryFn: fetchTokenPacks,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useAdvanceTokenPurchaseLimits = () => {
  return useQuery({
    queryKey: tokenPackKeys.purchaseLimits(),
    queryFn: fetchAdvanceTokenPurchaseLimits,
    staleTime: 60 * 1000,
  });
};

export const useCreateTokenPack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTokenPack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tokenPackKeys.lists() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error creating token pack:', error);
      throw error;
    },
  });
};

export const useUpdateTokenPack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTokenPack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tokenPackKeys.lists() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error updating token pack:', error);
      throw error;
    },
  });
};

export const useToggleTokenPackStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTokenPackStatus,
    onMutate: async (variables: UpdateTokenPackStatusInput) => {
      await queryClient.cancelQueries({ queryKey: tokenPackKeys.lists() });
      const previousTokenPacks = queryClient.getQueryData<TokenPack[]>(tokenPackKeys.lists());

      if (previousTokenPacks) {
        queryClient.setQueryData<TokenPack[]>(
          tokenPackKeys.lists(),
          previousTokenPacks.map((tokenPack) =>
            tokenPack.publicId === variables.publicId
              ? { ...tokenPack, isActive: variables.isActive }
              : tokenPack
          )
        );
      }

      return { previousTokenPacks };
    },
    onError: (error: AxiosError, _variables, context) => {
      if (context?.previousTokenPacks) {
        queryClient.setQueryData(tokenPackKeys.lists(), context.previousTokenPacks);
      }
      console.error('Error toggling token pack status:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tokenPackKeys.lists() });
    },
  });
};

export const useUpdateAdvanceTokenPurchaseLimits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAdvanceTokenPurchaseLimits,
    onSuccess: (data) => {
      queryClient.setQueryData(tokenPackKeys.purchaseLimits(), data);
      queryClient.invalidateQueries({ queryKey: tokenPackKeys.purchaseLimits() });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      console.error('Error updating advance token purchase limits:', error);
      throw error;
    },
  });
};
